import { P2GBox, P2GButton, P2GCardContainer, P2GGrid, P2GSticky } from '@parcel2go/core-ui';
import { BasketQuote, QuoteParcel } from '~/lib/types/basket-types';
import { makeStyles } from 'tss-react/mui';
import QuoteEditor from './editable-quotes';
import { useCallback, useEffect, useState } from 'react';
import { hasVAT, PrinterType, QuoteFilterOptions, quoteRequestParams, ServiceType, SortType } from '~/lib/quotes';
import { VatToggle } from './vat-toggle';
import QuoteFilterBase from './quote-filter-base';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTenant } from '@parcel2go/npm-p2g-app';
import { useTranslation } from 'react-i18next';
import { buildQuoteUrl } from '~/lib/utils/parse-parcels-url-string';
import { TranslationNamespaces } from '~/lib/constants';

const useStyles = makeStyles()(() => ({
  root: {
    // The card comes with padding but the prices need to span full width
    '& .MuiCardContent-root': {
      padding: '0 !important'
    }
  }
}));

function QuoteFilterContainer({
  filter,
  filteredQuotes,
  quotes,
  updateFilter,
  onChange,
  origin,
  destination,
  parcels,
  originPostcode,
  stickyFilterDisabled,
  destinationPostcode,
  onReset,
  wrapped,
  enforcedVatMode
}: {
  filter: QuoteFilterOptions;
  filteredQuotes: BasketQuote[];
  quotes: BasketQuote[];
  onClose?: () => void;
  updateFilter: (filter: QuoteFilterOptions) => void;
  origin?: string;
  destination?: string;
  parcels?: QuoteParcel[];
  originPostcode?: string;
  destinationPostcode?: string;
  stickyFilterDisabled?: boolean;
  onReset?: () => void | undefined;
  wrapped: boolean;
  onChange?: (
    origin: string,
    destination: string,
    parcels: QuoteParcel[],
    originPostcode?: string,
    destinationPostcode?: string,
    serviceType?: ServiceType,
    sortType?: SortType,
    printerTypes?: PrinterType[],
    estimatedDeliveryDays?: number[],
    ratings?: number[],
    incVat?: boolean
  ) => void;
  enforcedVatMode?: 'inc' | 'exc';
}) {
  const { classes } = useStyles();
  const [editQuote, setIsEditingQuote] = useState(false);
  const { settings } = useTenant();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [prevServiceType, setPrevServiceType] = useState<ServiceType>();

  const { quoteType } = quoteRequestParams(searchParams, settings);
  const { t } = useTranslation(TranslationNamespaces.QUOTE);

  useEffect(() => {
    const quoteTypeSameDay = quoteType === 'SameDay';
    const sameDayServiceType = filter.serviceType === 'SameDay';
    const sameDayPrevServiceType = prevServiceType === 'SameDay';

    if (!(quoteTypeSameDay || sameDayServiceType || sameDayPrevServiceType)) return;

    if (sameDayServiceType) {
      if (!originPostcode || !destinationPostcode) {
        setPrevServiceType(filter.serviceType);
        setIsEditingQuote(true);
        return;
      } else {
        setPrevServiceType(filter.serviceType);
        navigate(
          buildQuoteUrl(
            origin as string,
            originPostcode,
            destination as string,
            destinationPostcode,
            parcels as QuoteParcel[],
            filter.serviceType
          ),
          {
            state: {
              serviceType: filter?.serviceType,
              sortType: filter?.sortType,
              printerTypes: filter?.printerTypes,
              estimatedDeliveryDays: filter?.estimatedDeliveryDays,
              ratings: filter?.ratings,
              incVat: filter?.incVat
            }
          }
        );
        return;
      }
    }

    if (quoteTypeSameDay && sameDayPrevServiceType && !sameDayServiceType) {
      setPrevServiceType(filter.serviceType);
      navigate(
        buildQuoteUrl(
          origin as string,
          originPostcode,
          destination as string,
          destinationPostcode,
          parcels as QuoteParcel[],
          filter.serviceType
        ),
        {
          state: {
            serviceType: filter?.serviceType,
            sortType: filter?.sortType,
            printerTypes: filter?.printerTypes,
            estimatedDeliveryDays: filter?.estimatedDeliveryDays,
            ratings: filter?.ratings,
            incVat: filter?.incVat
          }
        }
      );
      return;
    }

    setPrevServiceType(filter.serviceType);
  }, [filter.serviceType]);

  const onChangeQuote = useCallback(
    (
      origin: string,
      destination: string,
      parcels: QuoteParcel[],
      originPostcode?: string,
      destinationPostcode?: string
    ) => {
      updateFilter({ ...filter, couriers: [], estimatedDeliveryDays: [], tags: [] });
      if (onChange) {
        onChange(
          origin,
          destination,
          parcels,
          originPostcode,
          destinationPostcode,
          filter.serviceType,
          filter.sortType,
          filter.printerTypes,
          filter.estimatedDeliveryDays,
          filter.ratings,
          filter.incVat
        );
      }
      setIsEditingQuote(false);
    },
    [updateFilter, filter, onChange]
  );

  const contents = (
    <>
      {quotes && quotes.length > 0 && quotes.some((quote) => hasVAT(quote)) && !enforcedVatMode && (
        <P2GBox mb={2}>
          <VatToggle selected={!!filter.incVat} onChange={() => updateFilter({ ...filter, incVat: !filter.incVat })} />
        </P2GBox>
      )}

      <P2GGrid container direction="column" gap={3}>
        {onChange && origin && destination && (
          <P2GGrid item>
            <QuoteEditor
              editQuote={editQuote}
              onEditQuote={setIsEditingQuote}
              origin={origin}
              originPostcode={originPostcode}
              destination={destination}
              destinationPostcode={destinationPostcode}
              parcels={parcels}
              filter={filter}
              updateFilter={updateFilter}
              onChange={onChangeQuote}
            />
          </P2GGrid>
        )}
        {!editQuote && (
          <>
            <P2GGrid item>
              <QuoteFilterBase
                origin={origin}
                destination={destination}
                filter={filter}
                onChange={updateFilter}
                filteredQuotes={filteredQuotes}
                quotes={quotes}
              />
            </P2GGrid>
            {wrapped && onReset && (
              <P2GGrid item>
                <P2GButton variant="tertiary" fullWidth onClick={onReset}>
                  {t('FILTER_CLEAR_FILTER_BUTTON', { defaultValue: 'Clear Filter' })}
                </P2GButton>
              </P2GGrid>
            )}
          </>
        )}
      </P2GGrid>

      {!editQuote && !wrapped && (
        <P2GSticky align="bottom">
          <P2GBox pt={2} pb={2}>
            <P2GGrid container spacing={2}>
              {onReset && (
                <P2GGrid item xs={6}>
                  <P2GButton variant="tertiary" fullWidth onClick={onReset}>
                    {t('FILTER_CLEAR_FILTER_BUTTON', { defaultValue: 'Clear Filter' })}
                  </P2GButton>
                </P2GGrid>
              )}
              <P2GGrid item flexGrow={1}>
                <P2GButton fullWidth>{t('FILTER_DONE_BUTTON', { defaultValue: 'Done' })}</P2GButton>
              </P2GGrid>
            </P2GGrid>
          </P2GBox>
        </P2GSticky>
      )}
    </>
  );

  return wrapped ? (
    <P2GCardContainer dataTestId="quote-filter" classNames={[classes.root]}>
      <P2GBox sx={(t) => ({ padding: t.spacing(2, 2, 3, 2) })}>
        {stickyFilterDisabled ? (
          contents
        ) : (
          <P2GBox style={{ maxHeight: `calc(100vh - 170px)`, overflowY: 'auto' }}>{contents}</P2GBox>
        )}
      </P2GBox>
    </P2GCardContainer>
  ) : (
    <P2GBox>{contents}</P2GBox>
  );
}

export default QuoteFilterContainer;
